import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { RecordSummaryDerived } from '@marnic/shared';
import { api } from '../lib/api';
import { CallAnalysisPanel } from '../calls/CallAnalysisPanel';

interface LeadDetailResponse {
  lead: {
    id: string;
    firstName: string;
    lastName: string;
    company: string | null;
    email: string | null;
    phoneE164: string;
    temperature: 'cold' | 'warm' | 'hot' | null;
    ownerId: string | null;
    createdAt: string;
  };
  recordSummary: {
    combinedSummary: string;
    sentimentTrend: string | null;
    latestIntent: 'high' | 'medium' | 'low' | null;
    callCount: number;
    generatedAt: string;
  } | null;
  derived: RecordSummaryDerived;
  callTimeline: Array<{
    id: string;
    startedAt: string;
    direction: 'inbound' | 'outbound';
    durationSeconds: number | null;
    disposition: string | null;
    status: string;
    analysis: {
      status: string;
      summary: string | null;
      sentimentOverall: string | null;
      suggestedDisposition: string | null;
      completedAt: string | null;
    } | null;
  }>;
}

const STALE_RISK_STYLE: Record<NonNullable<RecordSummaryDerived['staleRisk']>, string> = {
  none: 'bg-emerald-100 text-emerald-800',
  low: 'bg-sky-100 text-sky-800',
  medium: 'bg-amber-100 text-amber-800',
  high: 'bg-red-100 text-red-800',
};

function formatDuration(seconds: number | null): string {
  if (!seconds || seconds <= 0) return '0s';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m ? `${m}m${s ? ` ${s}s` : ''}` : `${s}s`;
}

export function LeadDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [openCallId, setOpenCallId] = useState<string | null>(null);

  const detailQuery = useQuery({
    queryKey: ['lead-detail', id],
    queryFn: () => api.get<LeadDetailResponse>(`/leads/${id}/detail`),
    enabled: !!id,
  });

  if (detailQuery.isLoading) return <div className="p-4 text-sm text-ink-500">Loading…</div>;
  if (detailQuery.isError || !detailQuery.data) {
    return (
      <div className="p-4 text-sm text-red-600">
        Could not load lead. <Link to="/leads" className="underline">Back to leads</Link>
      </div>
    );
  }

  const { lead, recordSummary, derived, callTimeline } = detailQuery.data;

  return (
    <div className="space-y-4">
      <header>
        <Link to="/leads" className="text-xs text-ink-500 hover:underline">← Back to leads</Link>
        <div className="mt-1 flex flex-wrap items-baseline gap-3">
          <h1 className="text-2xl font-semibold">{lead.firstName} {lead.lastName}</h1>
          {lead.company && <span className="text-ink-600">{lead.company}</span>}
          <span className="text-sm font-mono text-ink-500">{lead.phoneE164}</span>
          {lead.email && <span className="text-sm text-ink-500">{lead.email}</span>}
          {lead.temperature && (
            <span className="rounded bg-ink-100 px-2 py-0.5 text-[11px] uppercase tracking-wide text-ink-700">
              {lead.temperature}
            </span>
          )}
        </div>
      </header>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Rolling record summary */}
        <section className="card p-4 space-y-2">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-500">Record summary</h2>
            {recordSummary && (
              <span className="text-[10px] text-ink-400">
                generated {new Date(recordSummary.generatedAt).toLocaleString()}
              </span>
            )}
          </div>
          {recordSummary ? (
            <>
              <p className="text-sm leading-relaxed">{recordSummary.combinedSummary}</p>
              <div className="flex flex-wrap gap-2 text-[11px] text-ink-600">
                <span>Calls: {recordSummary.callCount}</span>
                {recordSummary.sentimentTrend && <span>Trend: {recordSummary.sentimentTrend}</span>}
                {recordSummary.latestIntent && <span>Latest intent: {recordSummary.latestIntent}</span>}
              </div>
            </>
          ) : (
            <p className="text-xs text-ink-500 italic">No record summary yet — awaiting first analysis.</p>
          )}
        </section>

        {/* Deal status — derived on-the-fly */}
        <section className="card p-4 space-y-2">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-500">Deal status</h2>
            {derived.staleRisk && (
              <span className={`rounded px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${STALE_RISK_STYLE[derived.staleRisk]}`}>
                stale risk: {derived.staleRisk}
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 text-[12px]">
            {derived.dealStage && (
              <div>
                <div className="text-[10px] uppercase text-ink-500">Stage</div>
                <div className="font-medium">{derived.dealStage}</div>
              </div>
            )}
            {derived.closeProbability !== null && (
              <div>
                <div className="text-[10px] uppercase text-ink-500">Close probability</div>
                <div className="font-medium">{Math.round(derived.closeProbability * 100)}%</div>
              </div>
            )}
            {derived.orderSize && (
              <div>
                <div className="text-[10px] uppercase text-ink-500">Order size</div>
                <div>{derived.orderSize}</div>
              </div>
            )}
            {derived.deadline && (
              <div>
                <div className="text-[10px] uppercase text-ink-500">Deadline</div>
                <div>{derived.deadline}</div>
              </div>
            )}
            {derived.decisionMaker && (
              <div>
                <div className="text-[10px] uppercase text-ink-500">Decision maker</div>
                <div>{derived.decisionMaker}</div>
              </div>
            )}
            {derived.competitor && (
              <div>
                <div className="text-[10px] uppercase text-ink-500">Competitor</div>
                <div>{derived.competitor}</div>
              </div>
            )}
            {derived.nextFollowUpDue && (
              <div>
                <div className="text-[10px] uppercase text-ink-500">Next follow-up</div>
                <div>{derived.nextFollowUpDue}</div>
              </div>
            )}
            {derived.lastMeaningfulContactDate && (
              <div>
                <div className="text-[10px] uppercase text-ink-500">Last meaningful contact</div>
                <div>{new Date(derived.lastMeaningfulContactDate).toLocaleDateString()}</div>
              </div>
            )}
          </div>
          {derived.blocker && (
            <div className="rounded bg-red-50 border border-red-100 px-2 py-1 text-[12px] text-red-800">
              <span className="font-semibold">Blocker:</span> {derived.blocker}
            </div>
          )}
          {derived.nextStep && (
            <div className="rounded bg-emerald-50 border border-emerald-100 px-2 py-1 text-[12px] text-emerald-900">
              <span className="font-semibold">Next step:</span> {derived.nextStep}
            </div>
          )}
          {derived.executiveAction && (
            <div className="rounded bg-purple-50 border border-purple-100 px-2 py-1 text-[12px] text-purple-900">
              <span className="font-semibold">Exec action:</span> {derived.executiveAction}
            </div>
          )}
          {derived.sourceCallCount === 0 && !derived.dealStage && (
            <p className="text-xs text-ink-500 italic">
              No succeeded analyses for this lead yet — derived fields will populate after the first call is
              analyzed.
            </p>
          )}
        </section>
      </div>

      {/* Call timeline */}
      <section className="card p-4 space-y-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-500">Call timeline</h2>
        {callTimeline.length === 0 ? (
          <p className="text-xs text-ink-500 italic">No calls yet.</p>
        ) : (
          <ol className="space-y-1">
            {callTimeline.map((call) => {
              const isOpen = openCallId === call.id;
              return (
                <li key={call.id} className="rounded border border-ink-100 bg-white">
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-ink-50"
                    onClick={() => setOpenCallId(isOpen ? null : call.id)}
                  >
                    <span className="text-[10px] uppercase font-mono text-ink-500 w-16">{call.direction}</span>
                    <span className="text-xs text-ink-700">
                      {new Date(call.startedAt).toLocaleString()}
                    </span>
                    <span className="text-xs text-ink-500">{formatDuration(call.durationSeconds)}</span>
                    {call.disposition && (
                      <span className="rounded bg-ink-100 px-1.5 py-0.5 text-[10px] capitalize">
                        {call.disposition.replace(/_/g, ' ')}
                      </span>
                    )}
                    <span className="ml-auto text-xs text-ink-700 truncate max-w-md">
                      {call.analysis?.summary ?? <span className="italic text-ink-400">no analysis</span>}
                    </span>
                    <span className="text-ink-400 text-xs">{isOpen ? '▼' : '▶'}</span>
                  </button>
                  {isOpen && (
                    <div className="border-t border-ink-100 p-3">
                      <CallAnalysisPanel callId={call.id} />
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </section>
    </div>
  );
}
